import { z } from 'zod';
import {
  AccessSimulationInputSchema,
  AccessSimulationSchema,
  AppearanceSchema,
  AuditChainReportSchema,
  AuditPageSchema,
  AuditQuerySchema,
  CatalogDraftSchema,
  ConnectionStatusSchema,
  ConnectionTestInputSchema,
  ConnectionTestResultSchema,
  ContextProposalSchema,
  EvalReportRecordSchema,
  FeedbackItemSchema,
  FeedbackStatusSchema,
  OverviewSchema,
  QualityWeekSchema,
  SessionSchema,
  TenantSettingsSchema,
} from './admin.js';
import { CatalogSnapshotSchema, PublicationSchema } from './catalog.js';
import { IdSchema } from './common.js';
import { APPS_ROUTES } from './apps.js';
import { CONTEXT_RUNS_ROUTES } from './context-runs.js';
import { LicenseStateSchema } from './license.js';
import { CONVERSATIONS_ROUTES } from './turns.js';
import { USERS_ROUTES } from './users.js';

/*
 * Mapa da API do painel (/api/admin). Uma definição só: o serviço valida com ela, o painel monta
 * o cliente tipado com ela e o OpenAPI sai dela. Mudar uma rota é mudar aqui.
 *
 * Acesso:
 *  - public: sem sessão (só o que a tela de login precisa);
 *  - session: qualquer pessoa logada (a resposta se limita ao que ela pode ver);
 *  - owner: administrador ou dono de algum assunto;
 *  - admin: papel de administrador (admin.role).
 * Toda rota que muda estado exige o cabeçalho x-csrf-token igual ao da sessão.
 */

export type Access = 'public' | 'session' | 'owner' | 'admin';

export interface AdminRoute {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH';
  /** Caminho a partir de /api/admin, com :param. */
  readonly path: string;
  readonly access: Access;
  readonly summary: string;
  readonly params?: z.ZodType;
  readonly query?: z.ZodType;
  readonly body?: z.ZodType;
  /** Resposta JSON. Rotas de arquivo (csv, zip) não declaram. */
  readonly response?: z.ZodType;
}

const IdParam = z.object({ id: z.string().min(1).max(64) });

export const RequiredApprovalSchema = z.object({ subject_id: IdSchema, papel: z.enum(['negocio', 'tecnico']), owner: z.string() });

export const DraftDetailSchema = z.object({
  draft: CatalogDraftSchema,
  status: z.object({ ready: z.boolean(), refused: z.boolean(), pending: z.array(RequiredApprovalSchema) }),
  /** O que cada pessoa logada pode aprovar neste rascunho. */
  can_approve: z.array(RequiredApprovalSchema),
});
export type DraftDetail = z.infer<typeof DraftDetailSchema>;

export const ValidationResultSchema = z.object({ ok: z.boolean(), errors: z.array(z.string()) });

export const SystemInfoSchema = z.object({
  version: z.string(),
  edition: z.enum(['selfhosted', 'cloud']),
  instance_id: z.string(),
  tenant_id: z.string(),
  uptime_seconds: z.number().int(),
  state_backend: z.enum(['files', 'postgres']),
  /** lastrouter.yaml sem segredos: só leitura no painel, quem muda é a TI. */
  config: z.record(z.string(), z.unknown()),
  license: LicenseStateSchema,
});
export type SystemInfo = z.infer<typeof SystemInfoSchema>;

export const ADMIN_ROUTES = {
  // Sessão
  publicAppearance: { method: 'GET', path: '/appearance', access: 'public', summary: 'Marca do cliente para a tela de login', response: AppearanceSchema },
  session: { method: 'GET', path: '/session', access: 'session', summary: 'Sessão atual', response: SessionSchema },
  devLogin: { method: 'POST', path: '/auth/dev', access: 'public', summary: 'Login de desenvolvimento com token do dev-token', body: z.object({ token: z.string().min(10).max(8000) }), response: SessionSchema },
  logout: { method: 'POST', path: '/auth/logout', access: 'session', summary: 'Encerra a sessão', response: z.object({ ok: z.literal(true) }) },

  // Visão geral, conexões, sistema
  overview: { method: 'GET', path: '/overview', access: 'admin', summary: 'Visão geral', response: OverviewSchema },
  connections: { method: 'GET', path: '/connections', access: 'admin', summary: 'Conexões com as fontes', response: z.array(ConnectionStatusSchema) },
  testConnection: { method: 'POST', path: '/connections/:id/test', access: 'admin', summary: 'Testa a fonte como um usuário de teste', params: IdParam, body: ConnectionTestInputSchema, response: ConnectionTestResultSchema },
  system: { method: 'GET', path: '/system', access: 'admin', summary: 'Versão, licença e configuração (sem segredos)', response: SystemInfoSchema },

  // Contexto
  catalog: { method: 'GET', path: '/catalog', access: 'owner', summary: 'Catálogo publicado', response: z.object({ publication: PublicationSchema.nullable(), snapshot: CatalogSnapshotSchema.nullable() }) },
  publications: { method: 'GET', path: '/catalog/publications', access: 'owner', summary: 'Histórico de publicações', response: z.array(PublicationSchema) },
  restorePublication: { method: 'POST', path: '/catalog/publications/:id/restore', access: 'admin', summary: 'Republica uma versão anterior', params: IdParam, body: z.object({ reason: z.string().min(1).max(500) }), response: PublicationSchema },
  validateCatalog: { method: 'POST', path: '/catalog/validate', access: 'owner', summary: 'Valida um catálogo sem gravar', body: z.object({ snapshot: z.unknown() }), response: ValidationResultSchema },
  drafts: { method: 'GET', path: '/catalog/drafts', access: 'owner', summary: 'Rascunhos', query: z.object({ status: z.enum(['aberto', 'aprovado', 'publicado', 'descartado']).optional() }), response: z.array(CatalogDraftSchema) },
  createDraft: { method: 'POST', path: '/catalog/drafts', access: 'owner', summary: 'Cria um rascunho a partir de um catálogo editado', body: z.object({ snapshot: z.unknown(), reason: z.string().min(1).max(500) }), response: CatalogDraftSchema },
  draft: { method: 'GET', path: '/catalog/drafts/:id', access: 'owner', summary: 'Rascunho com o estado das aprovações', params: IdParam, response: DraftDetailSchema },
  updateDraft: { method: 'PUT', path: '/catalog/drafts/:id', access: 'owner', summary: 'Atualiza o rascunho (zera as aprovações)', params: IdParam, body: z.object({ snapshot: z.unknown(), reason: z.string().min(1).max(500), expected_updated_at: z.string() }), response: CatalogDraftSchema },
  approveDraft: {
    method: 'POST',
    path: '/catalog/drafts/:id/approve',
    access: 'owner',
    summary: 'Aprovação ou recusa de um dono',
    params: IdParam,
    body: z.object({ subject_id: IdSchema, papel: z.enum(['negocio', 'tecnico']), decision: z.enum(['aprovado', 'recusado']), comment: z.string().max(2000).nullable().default(null) }),
    response: DraftDetailSchema,
  },
  publishDraft: { method: 'POST', path: '/catalog/drafts/:id/publish', access: 'admin', summary: 'Publica um rascunho aprovado', params: IdParam, response: PublicationSchema },
  discardDraft: { method: 'POST', path: '/catalog/drafts/:id/discard', access: 'owner', summary: 'Descarta um rascunho', params: IdParam, response: CatalogDraftSchema },
  exportCatalog: { method: 'GET', path: '/catalog/export', access: 'admin', summary: 'Catálogo publicado em zip (mesmo formato da pasta de contexto)' },
  importCatalog: { method: 'POST', path: '/catalog/import', access: 'owner', summary: 'Rascunho a partir de um zip da pasta de contexto', body: z.object({ zip_base64: z.string().min(1).max(20_000_000), reason: z.string().min(1).max(500) }), response: CatalogDraftSchema },

  // Acesso
  subjects: { method: 'GET', path: '/access/subjects', access: 'admin', summary: 'Assuntos com fonte e donos', response: z.array(z.object({ id: IdSchema, nome: z.string(), donos: z.object({ negocio: z.string(), tecnico: z.string() }), fonte: z.string() })) },
  simulateAccess: { method: 'POST', path: '/access/simulate', access: 'admin', summary: 'O que uma pessoa com estes papéis faria e quais apps veria na fonte', body: AccessSimulationInputSchema, response: AccessSimulationSchema },

  // Configurações de negócio (limites, canais, aparência)
  settings: { method: 'GET', path: '/settings', access: 'admin', summary: 'Configurações do painel', response: TenantSettingsSchema },
  putSettings: { method: 'PUT', path: '/settings', access: 'admin', summary: 'Grava as configurações (409 se outra pessoa gravou antes)', body: z.object({ settings: TenantSettingsSchema, expected_version: z.number().int().min(0) }), response: TenantSettingsSchema },
  checkAppearance: { method: 'POST', path: '/settings/appearance/check', access: 'admin', summary: 'Confere o contraste da cor de destaque', body: z.object({ accent_color: z.string() }), response: z.object({ problems: z.array(z.string()) }) },

  // Qualidade
  qualityWeekly: { method: 'GET', path: '/quality/weekly', access: 'owner', summary: 'Taxa de resposta, esclarecimentos e feedback por assunto e semana', query: z.object({ weeks: z.coerce.number().int().min(1).max(52).default(8) }), response: z.array(QualityWeekSchema) },
  feedback: { method: 'GET', path: '/quality/feedback', access: 'owner', summary: 'Feedback e pedidos de revisão', query: z.object({ status: FeedbackStatusSchema.optional(), subject_id: IdSchema.optional() }), response: z.array(FeedbackItemSchema) },
  updateFeedback: { method: 'PATCH', path: '/quality/feedback/:id', access: 'owner', summary: 'Muda o estado de um feedback', params: IdParam, body: z.object({ status: FeedbackStatusSchema, resolution: z.string().max(2000).nullable().default(null) }), response: FeedbackItemSchema },
  proposals: { method: 'GET', path: '/quality/proposals', access: 'owner', summary: 'Correções de contexto propostas', query: z.object({ status: z.enum(['proposta', 'aceita', 'recusada']).optional() }), response: z.array(ContextProposalSchema) },
  refreshProposals: { method: 'POST', path: '/quality/proposals/refresh', access: 'admin', summary: 'Gera propostas a partir das conversas com problema', response: z.array(ContextProposalSchema) },
  decideProposal: { method: 'POST', path: '/quality/proposals/:id/decide', access: 'owner', summary: 'Aceita ou recusa uma proposta (aceitar não muda o catálogo: abre a edição)', params: IdParam, body: z.object({ decision: z.enum(['aceita', 'recusada']) }), response: ContextProposalSchema },
  suiteCandidates: { method: 'GET', path: '/quality/candidates', access: 'owner', summary: 'Perguntas reais candidatas à suíte', response: z.array(z.object({ question: z.string(), reason: z.string() })) },
  evalReports: { method: 'GET', path: '/quality/eval-reports', access: 'admin', summary: 'Resultados da suíte', response: z.array(EvalReportRecordSchema.omit({ markdown: true })) },
  evalReport: { method: 'GET', path: '/quality/eval-reports/:id', access: 'admin', summary: 'Relatório completo da suíte', params: IdParam, response: EvalReportRecordSchema },

  // Auditoria
  audit: { method: 'GET', path: '/audit', access: 'admin', summary: 'Linhas de auditoria', query: AuditQuerySchema, response: AuditPageSchema },
  auditExport: { method: 'GET', path: '/audit/export.csv', access: 'admin', summary: 'Auditoria em CSV para o auditor', query: AuditQuerySchema.omit({ cursor: true, limit: true }) },
  auditVerify: { method: 'GET', path: '/audit/verify', access: 'admin', summary: 'Confere a corrente de hash da auditoria', response: AuditChainReportSchema },
  ...CONVERSATIONS_ROUTES,
  ...APPS_ROUTES,
  ...CONTEXT_RUNS_ROUTES,
  ...USERS_ROUTES,
} as const satisfies Record<string, AdminRoute>;

export type AdminRouteName = keyof typeof ADMIN_ROUTES;

/** Corpo de erro de toda rota do painel. */
export const AdminErrorSchema = z.object({ error: z.object({ code: z.string(), message: z.string(), correlation_id: z.string(), details: z.array(z.string()).optional() }) });

/** OpenAPI 3.1 gerado do mapa: a mesma definição que valida as rotas. */
export function adminOpenApi(version: string): Record<string, unknown> {
  const paths: Record<string, Record<string, unknown>> = {};
  for (const [name, route] of Object.entries(ADMIN_ROUTES) as [string, AdminRoute][]) {
    const path = `/api/admin${route.path.replace(/:([a-z_]+)/g, '{$1}')}`;
    const op: Record<string, unknown> = {
      operationId: name,
      summary: route.summary,
      'x-access': route.access,
      parameters: [
        ...[...route.path.matchAll(/:([a-z_]+)/g)].map((m) => ({ name: m[1], in: 'path', required: true, schema: { type: 'string' } })),
        ...(route.query ? Object.entries((z.toJSONSchema(route.query, { io: 'input', unrepresentable: 'any' }) as { properties?: Record<string, unknown> }).properties ?? {}).map(([n, s]) => ({ name: n, in: 'query', schema: s })) : []),
      ],
      responses: {
        '200': route.response ? { description: 'ok', content: { 'application/json': { schema: z.toJSONSchema(route.response, { unrepresentable: 'any' }) } } } : { description: 'arquivo' },
        default: { description: 'erro', content: { 'application/json': { schema: z.toJSONSchema(AdminErrorSchema) } } },
      },
    };
    if (route.body) op.requestBody = { required: true, content: { 'application/json': { schema: z.toJSONSchema(route.body, { io: 'input', unrepresentable: 'any' }) } } };
    paths[path] = { ...(paths[path] ?? {}), [route.method.toLowerCase()]: op };
  }
  return { openapi: '3.1.0', info: { title: 'Lastrouter: API do painel', version }, paths };
}
